import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {Card, Badge } from "flowbite-react";
import { useForm } from 'react-hook-form';
import { ClipboardList, Briefcase, Users, Download, User, MapPin, Phone, Mail, Calendar, Flag, Book, Heart, Plus, Edit, Printer, Eye, CheckSquare } from 'lucide-react';
import { useGetData } from '../../../../../hooks/useGetData';
import { EvaluadoInterface } from '../../../../../interfaces/EvaluadoInterface';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ThemeContext } from "../../../../../components/ThemeContext/ThemeContext";

export const PoligrafoListaScreen = () => {

  const { isDarkMode } = useContext(ThemeContext);
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { control, reset } = useForm();
  const navigate = useNavigate();

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '';

    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const generoMap: { [key: string]: string } = {
    M: 'Masculino',
    F: 'Femenino',
    O: 'Otro'
};

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ['evaluado'],
  });

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const [isOpen, setIsOpen] = useState(false);
  const toggleBlind = () => setIsOpen(!isOpen);

  const genero = queryEvaluado.data?.genero;

  useEffect(() => {  
    console.log(queryEvaluado.data);
    if (queryEvaluado.data) {
      reset({
        empresa: queryEvaluado.data.empresa?.nombre || '',
      });
    }
  }, [queryEvaluado.data, reset]);

  const modules = [
    { 
      title: 'Autorización al Proceso de Evaluaciones de Detección de Riesgos', 
      description: 'Autorización para el proceso de detección de riesgos',
      icon: <ClipboardList className="h-8 w-8" />,
      href: `/autorizacionDR/${evaluadosList_id}`,
      buttons: { 
        create: { show: true, href: `/crearAutDR/${evaluadosList_id}` }, 
        edit: { show: true, href: `/editarAutDR/${evaluadosList_id}` }, 
        printDownload: { show: false, href: `/autorizacionDR/${evaluadosList_id}` }, 
        view: { show: true, href: `/autorizacionDR/${evaluadosList_id}` }
      }
    },
    { 
      title: 'DatosGenerales', 
      description: 'Datos Generales del evaluado',
      icon: <ClipboardList className="h-8 w-8" />,
      href: `/DatosGenerales/${evaluadosList_id}`,
      buttons: { 
        create: { show: false, href: `/DatosGenerales/${evaluadosList_id}` }, 
        edit: { show: false, href: `/evaluadosList/editar/${evaluadosList_id}` }, 
        printDownload: { show: false, href: `/DatosGenerales/${evaluadosList_id}` }, 
        view: { show: true, href: `/DatosGenerales/${evaluadosList_id}` } 
      }
    },
    { 
      title: 'Conclusiones de Evaluaciones de Detección de Riesgos', 
      description: 'Conclusiones de la evaluación de detección de riesgos',
      icon: <CheckSquare className="h-8 w-8" />,
      href: `/conclusionesDR/${evaluadosList_id}`,
      buttons: { 
        create: { show: true, href: `/crearconclusionesDR/${evaluadosList_id}` }, 
        edit: { show: true, href: `/editarconclusionesDR/${evaluadosList_id}` }, 
        printDownload: { show: false, href: `/conclusionesDR/${evaluadosList_id}` }, 
        view: { show: true, href: `/conclusionesDR/${evaluadosList_id}` }  
      }
    },
  ];

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-800"}`}>
      {/* Botón para desplegar persiana */}
      <motion.div className="top-[80px] left-0 right-0 z-20">
        <motion.button
          onClick={toggleBlind}
          className={`w-full text-white py-3 rounded-b-lg focus:outline-none overflow-hidden ${isDarkMode ? 'bg-gradient-to-r from-blue-700 to-blue-800' : 'bg-gradient-to-r from-blue-500 to-blue-600'}`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <motion.div className="relative w-full h-8" initial={false} animate={isOpen ? "open" : "closed"}>
              <motion.div
                variants={{
                  open: { scaleX: 1.5, opacity: 0, transition: { duration: 0.5, } },
                  closed: { scaleX: 1, opacity: 0.5, transition: { duration: 0.5, } },
                }}
              />
            {/* Texto animado del botón */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center z-10 pointer-events-auto"
              variants={{
                open: { y: -20, opacity: 0 },
                closed: { y: 0, opacity: 1 },
              }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-lg font-semibold">Información del evaluado</span>
            </motion.div>
            <motion.div
              className="absolute inset-0 flex items-center justify-center z-10 pointer-events-auto"
              variants={{
                open: { y: 0, opacity: 1 },
                closed: { y: 20, opacity: 0 },
              }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-lg font-semibold">Cerrar información del evaluado</span>
            </motion.div>
          </motion.div>
        </motion.button>
      </motion.div>

      {/* Contenido desplegable */}
      <div className="top-[200px] left-0 right-0 z-10">
        <motion.div
          className={`shadow-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
          initial={false}
          animate={{ height: isOpen ? 'auto' : 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className={`p-6 space-y-6 rounded-xl shadow-xl transition-all duration-300 max-w-4xl mx-auto ${isDarkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <motion.div variants={sectionVariants}>
                    <h2 className={`text-3xl font-bold border-b-2 pb-2 mb-6 ${isDarkMode ? 'text-white border-blue-400' : 'text-gray-800 border-blue-500'}`}>
                      Evaluando a: {queryEvaluado.data?.nombre} {queryEvaluado.data?.apellido_paterno} {queryEvaluado.data?.apellido_materno}
                    </h2>
                  </motion.div>

                  <motion.div variants={sectionVariants}>
                    <Card className={`mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                      <h3 className={`text-xl font-semibold mb-4 flex items-center ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <User className={`mr-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} /> Información de Contacto
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <Mail className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                          <span className="font-medium">Correo:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.correo}</span>
                        </div>
                        <div className="flex items-center">
                          <Phone className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                          <span className="font-medium">Teléfono:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.telefono}</span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>

                  <motion.div variants={sectionVariants}>
                    <Card className={`mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                      <h3 className={`text-xl font-semibold mb-4 flex items-center ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <Briefcase className={`mr-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} /> Información Laboral
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <span className="font-medium">Empresa:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {queryEvaluado.data?.empresa_id?.nombre}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Puesto:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.puesto}</span>
                        </div>
                        <div className="flex items-center">
                          <Calendar className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                          <span className="font-medium">Fecha de Ingreso:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{formatDate(queryEvaluado.data?.fecha_ingreso)}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Antigüedad:</span>
                          <Badge color="success" className="ml-2">{queryEvaluado.data?.antiguedad} </Badge>
                        </div>
                      </div>
                    </Card>
                  </motion.div>

                  <motion.div variants={sectionVariants}>
                    <Card className={`mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                      <h3 className={`text-xl font-semibold mb-4 flex items-center ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <User className={`mr-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} /> Datos Personales
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <Calendar className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                          <span className="font-medium">Fecha de Nacimiento:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{formatDate(queryEvaluado.data?.fecha_nacimiento)}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Género:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {genero ? generoMap[genero] : 'No especificado'}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <MapPin className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                          <span className="font-medium">Lugar de Nacimiento:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.lugar_nacimiento}</span>
                        </div>
                        <div className="flex items-center">
                          <Flag className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                          <span className="font-medium">Nacionalidad:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.nacionalidad}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">CURP:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.curp}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">RFC:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.rfc}</span>
                        </div>
                        <div className="flex items-center">
                          <Book className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                          <span className="font-medium">Escolaridad:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.escolaridad}</span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>

                  <motion.div variants={sectionVariants}>
                    <Card className={`mb-6 ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                      <h3 className={`text-xl font-semibold mb-4 flex items-center ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <MapPin className={`mr-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} /> Dirección
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <span className="font-medium">Calle:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.calle}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Número Exterior:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.numero_exterior}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Número Interior:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.numero_interior || "N/A"}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Referencia:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.referencia}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Colonia:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.colonia}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Código Postal:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.codigo_postal}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Municipio:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.municipio}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">Estado:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.estado}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium">País:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.pais}</span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>

                  <motion.div variants={sectionVariants}>
                    <Card className={`${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                      <h3 className={`text-xl font-semibold mb-4 flex items-center ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <Users className={`mr-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} /> Información Adicional
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <span className="font-medium">Años viviendo en el domicilio:</span>
                          <Badge color="success" className="ml-2">{queryEvaluado.data?.anios_domicilio_viviendo} años</Badge>
                        </div>
                        <div className="flex items-center">
                          <Users className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                          <span className="font-medium">Personas viviendo en el domicilio:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.personas_vivienda}</span>
                        </div>
                        <div className="flex items-center">
                          <Heart className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                          <span className="font-medium">Estado Civil:</span>
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{queryEvaluado.data?.estado_civil}</span>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Botón "Regresar" */}
      <div className="flex justify-between items-center px-8 py-4">
        <button
          onClick={() => {
            window.scrollTo(0, 0);
            navigate(`/modulos/${queryEvaluado.data?.uuid}`)
          }}
          className={`py-2 px-4 rounded transition ${isDarkMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-red-500 hover:bg-red-600 text-white"}`}
        >
          Regresar
        </button>
      </div>

      {/* Resto del contenido */}
      <div className="container mx-auto">
        <h1 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Módulo de Evaluación de Detección de riesgos</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-4">
          {modules.map((module, index) => (
            <div 
              key={index} 
              className={`border rounded-lg p-4 shadow flex flex-col justify-between min-h-[280px] ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`flex items-center justify-center h-12 w-12 ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>
                  {module.icon}
                </div>
                <h3 className={`font-bold text-xl ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{module.title}</h3> 
              </div>
              <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{module.description}</p>
              <div className="flex justify-center space-x-4">
                {/* Botón de Crear Formulario */}
                {module.buttons.create.show && (
                  <Link 
                      to={module.buttons.create.href}
                      title="Crear Formulario" 
                      className={`flex items-center justify-center p-2 rounded ${isDarkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-500 hover:bg-blue-600 text-white"}`}
                      onClick={() => {
                          window.scrollTo(0, 0);
                          console.log('Crear formulario');
                      }}
                  >
                      <Plus className="h-5 w-5" />
                  </Link>
                )}

                {/* Botón de Editar Formulario */}
                {module.buttons.edit.show && (
                  <Link 
                      to={module.buttons.edit.href}
                      className={`flex items-center justify-center p-2 rounded ${isDarkMode ? "bg-green-600 hover:bg-green-700 text-white" : "bg-green-500 hover:bg-green-600 text-white"}`}
                      title="Editar Formulario"
                      onClick={() => {
                          window.scrollTo(0, 0);
                          console.log('Editar formulario');
                      }}
                  >
                      <Edit className="h-5 w-5" />
                  </Link>
                )}

                {/* Botón de Imprimir y Descargar Formulario */}
                {module.buttons.printDownload.show && (
                  <button 
                      className={`flex items-center justify-center space-x-2 p-2 rounded ${isDarkMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-red-500 hover:bg-red-600 text-white"}`}
                      title="Imprimir / Descargar Formulario"
                      onClick={() => {
                          window.print();
                          console.log('Imprimir formulario');
                          // Lógica adicional para descargar si es necesario
                      }}
                  >
                      <Printer className="h-5 w-5" />
                      <Download className="h-5 w-5" />
                  </button>
                )}

                {/* Botón de Visualizar */}
                {module.buttons.view.show && (
                  <Link 
                      to={module.buttons.view.href} 
                      className={`flex items-center justify-center p-2 rounded ${isDarkMode ? "bg-purple-600 hover:bg-purple-700 text-white" : "bg-purple-500 hover:bg-purple-600 text-white"}`}
                      title="Ver Formulario"
                      onClick={() => {
                          window.scrollTo(0, 0);
                          console.log('Visualizar formulario');
                      }}
                  >
                      <Eye className="h-5 w-5" />
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};